import React, { useReducer, createContext, useContext, ReactNode, useCallback, JSX } from 'react';

export type BreadCrumbsIcon =
    | 'communication'
    | 'program'
    | 'settings'
    | 'recruit'
    | 'campaign'
    | 'dtc-campaign'
    | 'product-seeding'
    | 'discount-code';

type Crumb = {
    label?: string;
    path?: string;
    icon?: BreadCrumbsIcon;
};
type BreadCrumbsState = {
    crumbs: Crumb[];
};

type BreadCrumbsAction =
    | {
          type: 'ADD-CRUMBS';
          payload: Crumb[];
      }
    | {
          type: 'CLEAR-CRUMBS';
          payload: null;
      }
    | {
          type: 'SET-CRUMBS';
          payload: Crumb[];
      };

type BreadCrumbsContextType = {
    state: BreadCrumbsState;
    dispatch: React.Dispatch<BreadCrumbsAction>;
    setBreadCrumbs: (crumb: Crumb[]) => void;
    clearBreadCrumbs: () => void;
    appendBreadCrumbs: (crumb: Crumb[]) => void;
};

const BreadCrumbsContext = createContext<BreadCrumbsContextType | undefined>(undefined);

const breadCrumbsReducer = (
    state: BreadCrumbsState,
    action: BreadCrumbsAction,
): BreadCrumbsState => {
    switch (action.type) {
        case 'ADD-CRUMBS': {
            // append crumbs to the existing crumbs
            return { crumbs: [...state.crumbs, ...action.payload] };
        }
        case 'CLEAR-CRUMBS': {
            return { crumbs: [] };
        }
        case 'SET-CRUMBS': {
            // clear the existing crumbs and set the new crumbs
            return { crumbs: action.payload };
        }
        default: {
            return state;
        }
    }
};

type BreadCrumbsProviderProps = {
    children: ReactNode;
};

const BreadCrumbsProvider = ({ children }: BreadCrumbsProviderProps): JSX.Element => {
    const [state, dispatch] = useReducer(breadCrumbsReducer, {
        crumbs: [],
    });
    const appendBreadCrumbs = useCallback((crumb: Crumb[]) => {
        dispatch({ type: 'ADD-CRUMBS', payload: crumb });
    }, []);
    const setBreadCrumbs = useCallback((crumb: Crumb[]) => {
        dispatch({ type: 'SET-CRUMBS', payload: crumb });
    }, []);
    const clearBreadCrumbs = useCallback(() => {
        dispatch({ type: 'CLEAR-CRUMBS', payload: null });
    }, []);
    const value = { state, dispatch, setBreadCrumbs, clearBreadCrumbs, appendBreadCrumbs };

    return <BreadCrumbsContext.Provider value={value}>{children}</BreadCrumbsContext.Provider>;
};

const useBreadCrumbs = (): BreadCrumbsContextType => {
    const context = useContext(BreadCrumbsContext);
    if (context === undefined) {
        throw new Error('useBreadCrumbs must be used within an BreadCrumbsProvider');
    }
    const { state, dispatch, setBreadCrumbs, clearBreadCrumbs, appendBreadCrumbs } = context;

    return { state, dispatch, setBreadCrumbs, clearBreadCrumbs, appendBreadCrumbs };
};

export { BreadCrumbsProvider, useBreadCrumbs };
